import React, { useContext, useState } from 'react'
import { Card, Button, Form, Row, Col, Container } from 'react-bootstrap'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { AuthContext } from '../../AuthContext'
import Cookies from 'js-cookie'

// 運動類型對照表
const EXERCISE_TYPES = {
  0: '游泳',
  1: '攀岩',
  2: '走路',
  3: '慢跑',
  4: '排球',
  5: '保齡球',
  6: '高爾夫球',
  7: '乒乓球',
  8: '棒壘球',
  9: '羽球',
  10: '網球',
  11: '足球',
  12: '籃球',
  13: '溜冰或直排輪',
  14: '腳踏車或飛輪',
  15: '瑜珈、太極等伸展律動性舞蹈',
  16: '有氧或快節奏舞蹈',
  99: '其他',
}

const AddExercise = ({ onSuccess, onCancel, date }) => {
  const { patient_id } = useParams()
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}

  // 初始化一筆空記錄
  const [records, setRecords] = useState([
    {
      startTime: '',
      endTime: '',
      exerciseType: '',
      comment: '',
    },
  ])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // 添加新的一筆記錄欄位
  const handleAddNewRow = () => {
    setRecords((prev) => [
      ...prev,
      {
        startTime: '',
        endTime: '',
        exerciseType: '',
        comment: '',
      },
    ])
  }

  // 處理輸入變更
  const handleInputChange = (index, field, value) => {
    setRecords((prev) =>
      prev.map((record, i) =>
        i === index ? { ...record, [field]: value } : record
      )
    )

    // 清除該欄位的錯誤訊息
    if (errors[`${field}_${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[`${field}_${index}`]
        return newErrors
      })
    }
  }

  // 驗證表單
  const validateForm = () => {
    const newErrors = {}
    let isValid = true

    records.forEach((record, index) => {
      if (!record.startTime) {
        newErrors[`startTime_${index}`] = '請輸入開始時間'
        isValid = false
      }
      if (!record.endTime) {
        newErrors[`endTime_${index}`] = '請輸入結束時間'
        isValid = false
      }
      if (!record.exerciseType && record.exerciseType !== 0) {
        newErrors[`exerciseType_${index}`] = '請選擇運動類型'
        isValid = false
      }

      // 檢查時間順序
      if (record.startTime && record.endTime) {
        const start = new Date(`${date} ${record.startTime}`)
        const end = new Date(`${date} ${record.endTime}`)
        if (start >= end) {
          newErrors[`time_${index}`] = '結束時間必須晚於開始時間'
          isValid = false
        }
      }
    })

    setErrors(newErrors)
    return isValid
  }

  // 提交新記錄
  const handleSubmit = async () => {
    if (!session) {
      setLoginShow(true)
      return
    }

    if (!validateForm()) return

    try {
      setIsSubmitting(true)

      // 格式化數據
      const exercise = records.map((record) => ({
        exercise_type: parseInt(record.exerciseType),
        start_time: `${date} ${record.startTime}:00`, // 添加秒數
        end_time: `${date} ${record.endTime}:00`, // 添加秒數
        calories: 0, // 默認值
        comment: record.comment || '',
      }))

      const response = await axios.post(
        `${baseUrl}/api/crm/patient/measure/addExercise`,
        {
          patient_id,
          exercise,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      if (response.data.message === '新增成功') {
        onSuccess?.()
        extendCookie()
      } else if (response?.data?.error?.code === '50004') {
        setLoginShow(true)
        throw new Error('Session expired')
      }
    } catch (error) {
      console.error('Error adding exercise records:', error)
      setErrors({ submit: '新增記錄失敗，請稍後再試' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <Button
          variant="link"
          onClick={onCancel}
          className="text-decoration-none text-secondary"
        >
          <h5>
            <i className="fa-solid fa-arrow-left me-2" />
            返回數據收集紀錄
          </h5>
        </Button>
      </div>

      <Card className="position-relative mb-4 rounded-3 p-4">
        {/* 日期和按鈕區域 */}
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="m-0">{date}</h4>
          <div>
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              取消
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '儲存中...' : '儲存'}
            </Button>
          </div>
        </div>

        {/* 記錄輸入區域 */}
        {records.map((record, index) => (
          <Row
            key={index}
            className="mt-3 g-3 justify-content-center align-items-center"
          >
            <Col md={2}>
              <Form.Group>
                <Form.Control
                  type="time"
                  value={record.startTime}
                  onChange={(e) =>
                    handleInputChange(index, 'startTime', e.target.value)
                  }
                  isInvalid={
                    !!errors[`startTime_${index}`] || !!errors[`time_${index}`]
                  }
                  placeholder="開始時間"
                />
                <Form.Control.Feedback type="invalid">
                  {errors[`startTime_${index}`] || errors[`time_${index}`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>{' '}
            ～{' '}
            <Col md={2}>
              <Form.Group>
                <Form.Control
                  type="time"
                  value={record.endTime}
                  onChange={(e) =>
                    handleInputChange(index, 'endTime', e.target.value)
                  }
                  isInvalid={!!errors[`endTime_${index}`]}
                  placeholder="結束時間"
                />
                <Form.Control.Feedback type="invalid">
                  {errors[`endTime_${index}`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Select
                  value={record.exerciseType}
                  onChange={(e) =>
                    handleInputChange(index, 'exerciseType', e.target.value)
                  }
                  isInvalid={!!errors[`exerciseType_${index}`]}
                >
                  <option value="">選擇運動類型</option>
                  {Object.entries(EXERCISE_TYPES).map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors[`exerciseType_${index}`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder="備註可留空"
                value={record.comment}
                onChange={(e) =>
                  handleInputChange(index, 'comment', e.target.value)
                }
              />
            </Col>
          </Row>
        ))}

        {/* 新增一筆按鈕 */}
        <div className="d-flex justify-content-center mt-3">
          <Button
            variant="primary"
            className="text-white text-decoration-none"
            onClick={handleAddNewRow}
            disabled={isSubmitting}
          >
            + 新增一筆
          </Button>
        </div>
      </Card>

      {errors.submit && (
        <div className="text-danger text-center mt-2">{errors.submit}</div>
      )}
    </Container>
  )
}

export default AddExercise
